import { PiggyBank } from 'lucide-react';
import { bankOf, type BankContent } from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, type NumberSpec } from './form';

type BankNumbers = Exclude<keyof BankContent, 'visible' | 'name' | 'short'>;
const RATES: NumberSpec<BankNumbers>[] = [
  { key: 'savingsBps', label: 'Savings interest a week', unit: 'bps', min: 0, max: 5_000 },
  { key: 'interestCapPence', label: 'Interest paid on savings up to', unit: 'pounds', min: 0, max: 100_000_000_000 },
  { key: 'bondBps', label: 'A bond pays back over its term', unit: 'bps', min: 0, max: 5_000 },
  { key: 'bondDays', label: 'Bond term (days)', unit: 'number', min: 1, max: 90, whole: true },
];
const LIMITS: NumberSpec<BankNumbers>[] = [
  { key: 'minMovePence', label: 'Smallest deposit or withdrawal', unit: 'pounds', min: 0, max: 1_000_000_000 },
  { key: 'movesPerDay', label: 'Deposits and withdrawals a day', unit: 'number', min: 1, max: 200, whole: true },
  { key: 'minBondPence', label: 'Smallest bond', unit: 'pounds', min: 0, max: 1_000_000_000 },
  { key: 'maxBonds', label: 'Most bonds open at once', unit: 'number', min: 1, max: 50, whole: true },
  { key: 'bondMaxPence', label: 'Most in bonds at once', unit: 'pounds', min: 0, max: 100_000_000_000 },
  { key: 'bondsPerDay', label: 'Bonds bought a day', unit: 'number', min: 1, max: 100, whole: true },
];

// the Bank (as Lagos Life's Naija Reserve Bank): its name, the savings and bond rates, and the limits. A new
// rate applies from the moment it is published. Part of the content draft, published with it.
export function BankPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const bank = bankOf(working ?? {});
  const change = (fn: (draft: BankContent) => void) =>
    editor.edit((content) => {
      content.bank ??= structuredClone(bankOf(content));
      fn(content.bank);
    });
  const numbers = (specs: NumberSpec<BankNumbers>[]) =>
    specs.map((spec) => (
      <NumberField
        key={spec.key}
        id={`bank-${spec.key}`}
        spec={spec}
        value={bank[spec.key]}
        onChange={(value) => change((draft) => void (draft[spec.key] = value))}
      />
    ));
  return (
    <section>
      <PageHeader
        title="Bank"
        description="The players' bank (as Lagos Life's Naija Reserve Bank): savings interest, bonds and their limits. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The bank">
            <h2 className="content-subtitle">
              <PiggyBank size={16} aria-hidden="true" /> The bank
            </h2>
            <label className="designer-check">
              <Switch
                checked={bank.visible}
                aria-label="Bank open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can use the bank
            </label>
            <div className="content-grid">
              <Field id="bank-name" label="Name">
                <Input
                  id="bank-name"
                  value={bank.name}
                  maxLength={40}
                  onChange={(e) => change((draft) => void (draft.name = e.target.value))}
                />
              </Field>
              <Field id="bank-short" label="Short name">
                <Input
                  id="bank-short"
                  value={bank.short}
                  maxLength={20}
                  onChange={(e) => change((draft) => void (draft.short = e.target.value))}
                />
              </Field>
            </div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Rates">
            <h2 className="content-subtitle">Rates</h2>
            <div className="content-grid">{numbers(RATES)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Limits">
            <h2 className="content-subtitle">Limits</h2>
            <div className="content-grid">{numbers(LIMITS)}</div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
